import { readCsvFileAsync, readXlsxFileAsync } from '@vestigas/utils';

import { LIST_SEPARATOR } from './constants';
import {
  hasInconsistentFieldCountError,
  isCsvParseResult,
  isUnsupportedDelimiterError,
  SUPPORTED_CSV_DELIMITERS,
} from './dataImport.helpers';

export type ParsedImportFile = {
  fileContent: Array<Record<string, unknown>>;
  headerFields: string[];
  previewFileContent: Array<Record<string, unknown>>;
};

export type DataImportParserContext = {
  csvParseFailedMessage?: string;
  inconsistentFieldCountMessage?: string;
  unsupportedDelimiterMessage?: string;
};

export type DataImportParser = ((
  file: File,
  context: DataImportParserContext,
) => Promise<ParsedImportFile>) & {
  /**
   * Delimiters a CSV parser guesses between, so copy that names the accepted
   * separators reads them off the parser enforcing them rather than restating
   * a list that can drift. Absent on formats without delimiters.
   */
  readonly delimiters?: readonly string[];
};

/**
 * Maps a normalized, lowercase, dot-free file extension (e.g. `"csv"`) to
 * the parser responsible for it.
 */
export type DataImportParserRegistry = Record<string, DataImportParser>;

/**
 * Delimiters the registry's CSV parser accepts, or `undefined` when it
 * registers none, which leaves the shared default to the caller formatting it.
 */
export function csvDelimitersOf(
  parsers: Record<string, DataImportParser | undefined>,
): readonly string[] | undefined {
  return parsers.csv?.delimiters;
}

/** Brings a registry key or a filename suffix into the registry's key form. */
export function normalizeParserExtension(extension: string): string {
  return extension.trim().toLowerCase().replace(/^\./, '');
}

export type CreateCsvParserOptions = {
  /** Delimiters papaparse guesses between. Defaults to {@link SUPPORTED_CSV_DELIMITERS}. */
  delimitersToGuess?: readonly string[];
  /**
   * Separator that splits a cell value into a list. Defaults to
   * {@link LIST_SEPARATOR}; pass `null` to disable list splitting entirely.
   */
  listSeparator?: string | null;
};

export function createCsvParser(
  options: CreateCsvParserOptions = {},
): DataImportParser {
  const delimitersToGuess =
    options.delimitersToGuess ?? SUPPORTED_CSV_DELIMITERS;
  const listSeparator =
    options.listSeparator === undefined
      ? LIST_SEPARATOR
      : options.listSeparator;

  const parse: DataImportParser = async (file, context) => {
    const csvParseResult: unknown = await readCsvFileAsync(file, {
      delimitersToGuess: [...delimitersToGuess],
      // TODO: There was a product decision not to allow lists in cells,
      // but we should check if this is still the case for older files.
      // This should be removed ASAP when possible.
      transform(value: string) {
        if (listSeparator === null) {
          return value.trim();
        }

        return value.includes(listSeparator)
          ? value.split(listSeparator).map((item) => item.trim())
          : value.trim();
      },
      transformHeader(header: string) {
        return header.trim();
      },
    });

    if (!isCsvParseResult(csvParseResult)) {
      throw new Error(
        context.csvParseFailedMessage?.trim() || 'Failed to parse CSV file.',
      );
    }

    if (hasInconsistentFieldCountError(csvParseResult)) {
      throw new Error(
        context.inconsistentFieldCountMessage?.trim() ||
          'Rows have inconsistent column counts.',
      );
    }

    if (isUnsupportedDelimiterError(csvParseResult, delimitersToGuess)) {
      throw new Error(
        context.unsupportedDelimiterMessage?.trim() ||
          'Unsupported CSV delimiter.',
      );
    }

    return {
      fileContent: csvParseResult.data,
      headerFields: csvParseResult.meta?.fields ?? [],
      previewFileContent: csvParseResult.data,
    };
  };

  return Object.assign(parse, { delimiters: delimitersToGuess });
}

export type CreateXlsxParserOptions = {
  /** Selects a sheet by name or zero-based index. Defaults to the first sheet. */
  sheet?: string | number;
};

export function createXlsxParser(
  options: CreateXlsxParserOptions = {},
): DataImportParser {
  return async (file) => {
    const parsedRows = await readXlsxFileAsync(file, { sheet: options.sheet });

    return {
      fileContent: parsedRows.data,
      headerFields: parsedRows.headers,
      previewFileContent: parsedRows.displayData,
    };
  };
}

export const defaultDataImportParsers: DataImportParserRegistry = {
  csv: createCsvParser(),
  xlsx: createXlsxParser(),
};

/**
 * Effective registry for an import: an override wins for its own extension and
 * leaves every other default in place, so registering one extra format never
 * makes the built-in CSV and XLSX formats unreadable. Override keys are
 * normalized, so `".CSV"` replaces the default CSV parser instead of adding an
 * entry no lookup can reach.
 */
export function mergeDataImportParsers(
  overrides?: DataImportParserRegistry,
): DataImportParserRegistry {
  if (!overrides) return defaultDataImportParsers;

  return {
    ...defaultDataImportParsers,
    ...Object.fromEntries(
      Object.entries(overrides).map(([extension, parser]) => [
        normalizeParserExtension(extension),
        parser,
      ]),
    ),
  };
}
